/**
 * Utility functions for debugging
 */

// Check if we're in development mode
const isDev = process.env.NODE_ENV === "development"

/**
 * Log a debug message to the console (only in development)
 */
export function debug(...args: any[]): void {
  if (isDev) {
    console.log("[DEBUG]", ...args)
  }
}

/**
 * Log an error message to the console
 */
export function error(...args: any[]): void {
  console.error("[ERROR]", ...args)
}

/**
 * Log a warning message to the console
 */
export function warn(...args: any[]): void {
  console.warn("[WARN]", ...args)
}

/**
 * Log an info message to the console
 */
export function info(...args: any[]): void {
  console.info("[INFO]", ...args)
}

/**
 * Measure the execution time of a function
 * @param fn Function to measure
 * @param label Label to use in the log
 * @returns Result of the function
 */
export async function timing<T>(fn: () => Promise<T>, label: string): Promise<T> {
  const start = performance.now()
  try {
    return await fn()
  } finally {
    const end = performance.now()
    debug(`${label} took ${(end - start).toFixed(2)}ms`)
  }
}

/**
 * Create a group of logs
 * @param label Label for the group
 * @param fn Function to execute in the group
 * @returns Result of the function
 */
export async function group<T>(label: string, fn: () => Promise<T>): Promise<T> {
  console.group(label)
  try {
    return await fn()
  } finally {
    console.groupEnd()
  }
}
